'use client';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { SwiperBlock as ISwiperBlock } from '../types';
import Image from 'next/image';
import Heading from '../elements/Heading';
import NavArrow from '@/assets/nav_arrow.svg';
import ContentContainer from '../layout/ContentContainer';

// You can expand this as needed
const swiperThemes = {
  purple: {
    bg: 'bg-indigo-800',
    text: 'text-white',
    stat: 'text-white',
    arrow: 'text-white',
  },
  white: {
    bg: 'bg-white',
    text: 'text-indigo-800',
    stat: 'text-indigo-800',
    arrow: 'text-indigo-800', // Use text color since SVG uses currentColor
  },
  // None to be used when there is a background image
  clearBGDarkText: {
    bg: 'bg-transparent',
    text: 'text-indigo-800',
    stat: 'text-indigo-800',
    arrow: 'text-indigo-800', // Use text color since SVG uses currentColor
  },
  clearBGLightText: {
    bg: 'bg-transparent',
    text: 'text-white',
    stat: 'text-white',
    arrow: 'text-white', // Use text color since SVG uses currentColor
  },
  // Add more themes as needed
};

export default function SwiperBlock({ block }: { block: ISwiperBlock }) {
  // Default to 'purple' if not set
  const theme = swiperThemes[block.theme || 'purple'];

  return (
    <div className={`flex justify-center py-5 ${theme.bg}`}>
      <ContentContainer additionalClassNames="relative w-full">
        {/* Section 1: Left Arrow */}
        <div className="absolute top-1/2 left-4 z-10 -translate-y-1/2 sm:left-4 md:left-6 lg:left-10">
          <button className="custom-swiper-prev" type="button">
            <NavArrow className={`h-6 w-6 ${theme.arrow}`} />
          </button>
        </div>

        {/* Section 2: Container for Cards */}
        <div className="w-full">
          <Swiper
            modules={[Navigation]}
            navigation={{
              prevEl: '.custom-swiper-prev',
              nextEl: '.custom-swiper-next',
            }}
            slidesPerView={4}
            spaceBetween={10}
            breakpoints={{
              1024: {
                slidesPerView: 4,
              },
              768: {
                slidesPerView: 3,
              },
              0: {
                slidesPerView: 2,
                spaceBetween: 5,
              },
            }}
            loop
          >
            {block.slides.map((slide, idx) => (
              <SwiperSlide key={idx}>
                <div
                  className={`flex flex-col items-center justify-center text-center ${theme.text}`}
                >
                  {slide.image && (
                    <Image
                      src={slide.image}
                      alt={slide.title.text || ''}
                      width={70}
                      height={70}
                    />
                  )}
                  <div className="font-lora text-[36px] font-semibold">
                    {slide.stat}
                  </div>
                  <Heading
                    text={slide.title.text}
                    variant={slide.title.variant || 'h6'}
                    className="font-inter text-[12px] font-semibold"
                  />
                  {slide.content && (
                    <div
                      className="mt-2 text-center text-sm"
                      dangerouslySetInnerHTML={{ __html: slide.content }}
                    />
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Section 3: Right Arrow */}
        <div className="absolute top-1/2 right-4 z-10 -translate-y-1/2 sm:right-4 md:right-6 lg:right-10">
          <button className="custom-swiper-next" type="button">
            <NavArrow className={`h-6 w-6 rotate-180 ${theme.arrow}`} />
          </button>
        </div>
      </ContentContainer>
      <style jsx global>{`
        .swiper-button-next,
        .swiper-button-prev {
          display: none !important;
        }
      `}</style>
    </div>
  );
}
